import type { APIMessage } from '../contracts/chat-types.js';
import { compareSnowflakes } from '../contracts/snowflake.js';
import { emptyMessageList, type MessageList, type Snowflake } from './state.js';

export type FetchQuery = {
  limit: number;
  before?: Snowflake;
  after?: Snowflake;
  around?: Snowflake;
};

function sortedUnique(ids: Iterable<Snowflake>): Snowflake[] {
  return [...new Set(ids)].sort(compareSnowflakes);
}

/**
 * Junta mensagens vindas da REST (mais nova primeiro, como o Discord) ao
 * trecho carregado do canal. Sem `before`/`after`/`around`: o fim do canal
 * (substitui o trecho). `around`: pulo para uma mensagem (substitui).
 */
export function mergeFetched(
  list: MessageList | undefined,
  fetched: APIMessage[],
  query: FetchQuery,
): MessageList {
  const current = list ?? emptyMessageList();
  const full = fetched.length >= query.limit;
  const byId = { ...(query.before || query.after ? current.byId : {}) };
  for (const message of fetched) byId[message.id] = message;
  const fetchedIds = fetched.map((message) => message.id);

  if (query.around) {
    const half = Math.floor(query.limit / 2);
    const before = fetchedIds.filter(
      (id) => compareSnowflakes(id, query.around as string) < 0,
    ).length;
    const after = fetchedIds.filter(
      (id) => compareSnowflakes(id, query.around as string) > 0,
    ).length;
    return {
      ids: sortedUnique(fetchedIds),
      byId,
      hasMoreBefore: before >= half,
      hasMoreAfter: after >= query.limit - half - 1,
      loaded: true,
    };
  }
  if (query.before) {
    return {
      ...current,
      ids: sortedUnique([...fetchedIds, ...current.ids]),
      byId,
      hasMoreBefore: full,
      loaded: true,
    };
  }
  if (query.after) {
    return {
      ...current,
      ids: sortedUnique([...current.ids, ...fetchedIds]),
      byId,
      hasMoreAfter: full,
      loaded: true,
    };
  }
  return {
    ids: sortedUnique(fetchedIds),
    byId,
    hasMoreBefore: full,
    hasMoreAfter: false,
    loaded: true,
  };
}

/** Mensagem nova ou atualizada; só entra no trecho que chega ao fim do canal. */
export function upsertMessage(
  list: MessageList | undefined,
  message: APIMessage,
  options: { append: boolean },
): MessageList | undefined {
  if (!list) return list;
  if (list.byId[message.id]) {
    return {
      ...list,
      byId: {
        ...list.byId,
        [message.id]: { ...list.byId[message.id], ...message },
      },
    };
  }
  if (!options.append || list.hasMoreAfter || !list.loaded) return list;
  return {
    ...list,
    ids: sortedUnique([...list.ids, message.id]),
    byId: { ...list.byId, [message.id]: message },
  };
}

export function removeMessages(
  list: MessageList | undefined,
  ids: Snowflake[],
): MessageList | undefined {
  if (!list) return list;
  const remove = new Set(ids.filter((id) => list.byId[id]));
  if (!remove.size) return list;
  const byId = { ...list.byId };
  for (const id of remove) delete byId[id];
  return { ...list, ids: list.ids.filter((id) => !remove.has(id)), byId };
}

export function updateMessage(
  list: MessageList | undefined,
  id: Snowflake,
  update: (message: APIMessage) => APIMessage,
): MessageList | undefined {
  const message = list?.byId[id];
  if (!list || !message) return list;
  return { ...list, byId: { ...list.byId, [id]: update(message) } };
}

/**
 * Guarda só as `keep` mensagens mais novas (ao sair de um canal grande, para
 * a memória não crescer sem fim). O trecho volta a ter "mais antes".
 */
export function trimToNewest(list: MessageList, keep: number): MessageList {
  if (list.ids.length <= keep || list.hasMoreAfter) return list;
  const ids = list.ids.slice(list.ids.length - keep);
  const byId: Record<Snowflake, APIMessage> = {};
  for (const id of ids) byId[id] = list.byId[id];
  return { ...list, ids, byId, hasMoreBefore: true };
}
